import { useMemo } from 'react'

import { type OrgBusiness, updateBusiness, type UpdateBusinessData } from '../../models/business'
import { applyServerErrors } from './formServerErrors'
import { type SaveResult, type SyncedForm, useSyncedForm } from './useSyncedForm'

export type { SaveResult }

export const ENTITY_TYPE_OPTIONS = [
  { label: 'Individual', value: 'individual' },
  { label: 'Partnership', value: 'partnership' },
  { label: 'Corporation/LLC', value: 'llc' },
]

// Mirrors ELD_PROVIDERS in backend/insure/services/insurance/actions/eld_device_action.py.
export const ELD_PROVIDERS = [
  { label: 'Motive', value: 'motive' },
  { label: 'Samsara', value: 'samsara' },
  { label: 'Geotab', value: 'geotab' },
  { label: 'Verizon Connect', value: 'verizon_connect' },
]

export const ELD_NONE = 'none'
export const ELD_OTHER = 'other'

export interface BusinessDetailsSource {
  business: OrgBusiness | null | undefined
  entityType?: string | null
  /** False is the answer "no ELD"; null or absent means nobody has answered yet. */
  hasEld?: boolean | null
  eldProvider?: string | null
  coverageLapseDetected?: boolean
  cancellationReason?: string | null
}

export interface BusinessDetailsValues {
  name: string
  dotNumber: string
  mcNumber: string
  line1: string
  line2: string
  city: string
  state: string
  zip: string
  yearsInBusiness: string
  caNumber: string
  entityType: string
  /** '' while unanswered, ELD_NONE, ELD_OTHER, or one of ELD_PROVIDERS. */
  eldProvider: string
  eldProviderOther: string
  cancellationReason: string
}

export type BusinessDetailsField = keyof BusinessDetailsValues

export interface BusinessDetailsForm extends SyncedForm<BusinessDetailsValues> {
  save: (orgId: string, options: { onlyChanged: boolean }) => Promise<SaveResult>
}

const ADDRESS_FIELDS = ['line1', 'line2', 'city', 'state', 'zip'] as const

const ERROR_FIELDS: Record<string, BusinessDetailsField> = {
  name: 'name',
  dot_number: 'dotNumber',
  mc_number: 'mcNumber',
  ca_number: 'caNumber',
  years_in_business: 'yearsInBusiness',
  line1: 'line1',
  line2: 'line2',
  city: 'city',
  state: 'state',
  zip: 'zip',
  entity_type: 'entityType',
  eld_provider: 'eldProvider',
  cancellation_reason: 'cancellationReason',
}

export function businessDetailsValues(source: BusinessDetailsSource): BusinessDetailsValues {
  const { business, entityType, hasEld, eldProvider, cancellationReason } = source
  // A stored provider outside the known four came from the chat card's "other" input.
  const isKnownEld = ELD_PROVIDERS.some((provider) => provider.value === eldProvider)
  return {
    name: business?.name || '',
    dotNumber: business?.dotNumber || '',
    mcNumber: business?.mcNumber || '',
    line1: business?.address?.line1 || '',
    line2: business?.address?.line2 || '',
    city: business?.address?.city || '',
    state: business?.address?.state || '',
    zip: business?.address?.zip || '',
    yearsInBusiness: business?.yearsInBusiness?.toString() || '',
    caNumber: business?.caNumber || '',
    entityType: entityType || '',
    eldProvider: eldProvider ? (isKnownEld ? eldProvider : ELD_OTHER) : hasEld === false ? ELD_NONE : '',
    eldProviderOther: eldProvider && !isKnownEld ? eldProvider : '',
    cancellationReason: cancellationReason || '',
  }
}

/** The ELD answer the form holds, or undefined while the question is still unanswered. */
function eldAnswer(values: BusinessDetailsValues): string | undefined {
  if (!values.eldProvider) return undefined
  if (values.eldProvider === ELD_NONE) return ''
  if (values.eldProvider === ELD_OTHER) return values.eldProviderOther.trim()
  return values.eldProvider
}

/** What to PATCH to `business/`: the whole form, or with `changed` only those fields. */
export function businessDetailsPayload(
  values: BusinessDetailsValues,
  source: BusinessDetailsSource,
  changed?: readonly BusinessDetailsField[]
): UpdateBusinessData {
  const include = (field: BusinessDetailsField) => !changed || changed.includes(field)
  const payload: UpdateBusinessData = {}
  if (include('name')) payload.name = values.name
  if (include('dotNumber')) payload.dotNumber = values.dotNumber
  // The ELD and MC number go only when they changed: editing anything else must not answer for them.
  if (include('mcNumber') && values.mcNumber !== (source.business?.mcNumber || '')) {
    payload.mcNumber = values.mcNumber
  }
  if (include('yearsInBusiness')) {
    payload.yearsInBusiness = values.yearsInBusiness ? Number(values.yearsInBusiness) : null
  }
  const address: NonNullable<UpdateBusinessData['address']> = {}
  for (const field of ADDRESS_FIELDS) {
    if (include(field)) address[field] = values[field]
  }
  if (Object.keys(address).length > 0) payload.address = address
  if (include('entityType') && values.entityType) payload.entityType = values.entityType
  const nextEld = eldAnswer(values)
  const eldOnFile = source.eldProvider || (source.hasEld === false ? '' : undefined)
  const eldTouched = include('eldProvider') || include('eldProviderOther')
  if (eldTouched && nextEld !== undefined && nextEld !== eldOnFile) payload.eldProvider = nextEld
  if (source.coverageLapseDetected && include('cancellationReason')) {
    payload.cancellationReason = values.cancellationReason
  }
  if (include('caNumber')) payload.caNumber = values.caNumber
  return payload
}

export function useBusinessDetailsForm(source: BusinessDetailsSource): BusinessDetailsForm {
  const { business, entityType, hasEld, eldProvider, coverageLapseDetected, cancellationReason } = source
  const values = useMemo(
    () =>
      businessDetailsValues({
        business,
        entityType,
        hasEld,
        eldProvider,
        coverageLapseDetected,
        cancellationReason,
      }),
    [business, entityType, hasEld, eldProvider, coverageLapseDetected, cancellationReason]
  )
  const synced = useSyncedForm(values)

  async function save(orgId: string, { onlyChanged }: { onlyChanged: boolean }): Promise<SaveResult> {
    const { form, changedFields, markSaved } = synced
    form.clearErrors()
    if (onlyChanged && changedFields.length === 0) return { ok: true }
    if (!(await form.trigger())) return { ok: false }
    try {
      await updateBusiness(
        orgId,
        businessDetailsPayload(form.getValues(), source, onlyChanged ? changedFields : undefined)
      )
      markSaved()
      return { ok: true }
    } catch (error) {
      applyServerErrors(form, error, ERROR_FIELDS)
      return { ok: false, error }
    }
  }

  return { ...synced, save }
}
